import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp} from '@utils';
import {metaInfo} from '@configs';
import {HOME, SITEMAP} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {useDispatch} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {tagService} from '@jobseeker/common-core/services';
import type {GetServerSidePropsContext} from 'next/types';
import CharactersList from '@components/Sitemap/JobCategories/CharactersList';
import {Box, Grid, Typography} from '@mui/material';
import {Link} from '@jobseeker/lotus/elements';

type SitemapProps = {
  seoConfig: object;
  metaData: object;
  char: string;
  urls: {tagUrl: string; tagName: string; tagAndLocationUrl: string}[];
  canonicalUrl: string;
};

const JobTitle = ({
  seoConfig,
  metaData,
  char,
  urls,
  canonicalUrl,
}: SitemapProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: 'Sitemap',
      item: `/sitemap`,
    },
    {
      name: `Jobs Title - ${char.toUpperCase()}`,
      item: '',
    },
  ];

  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout
        seoConfig={{
          ...seoConfig,
          canonical: `${
            (seoConfig as {canonical?: string}).canonical
          }${canonicalUrl.replace('/sitemap', '')}`,
        }}
      />
      <NavLayout>
        <ResponsiveLayout
          breadcrumbElements={breadcrumbElements}
          variant={VersionVariants.VERSION_TWO}
        >
          <Box
            sx={{
              mx: isAppIimjobs ? 0 : 2,
            }}
          >
            <Typography component="h2" mb={2} mt={isAppIimjobs ? 0 : 6}>
              Jobs Title - {char.toUpperCase()}
            </Typography>
            <CharactersList />
            <Grid container>
              {urls.map((url, index) => (
                <Grid item key={index} xs={4}>
                  <Link href={url.tagUrl} target="_blank">
                    {url.tagName} Jobs
                  </Link>
                  &nbsp;{'>'}&nbsp;
                  <Link href={url.tagAndLocationUrl} target="_blank">
                    Location
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Box>
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const canonicalUrl = context?.resolvedUrl;
    const {slug = ''} = context.query;
    const char =
      String(slug)
        .replace(/\.html/, '')
        .split('-')
        .pop() ?? 'A';

    const metaData = getConfigByApp(metaInfo, HOME, true, SITEMAP);

    const seoConfig = await loadSEO(SITEMAP);

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: SITEMAP},
    });

    const baseUrl = process.env.NEXT_PUBLIC_LOAD_CONFIG;

    const keywords: {id: number; tagName: string; tagSlug: string}[] =
      (await tagService.getTags('', {char, categoryId: 0})) ?? [];

    const urls: {tagUrl: string; tagName: string; tagAndLocationUrl: string}[] =
      [];

    keywords?.forEach(({id, tagName, tagSlug}) => {
      const tagUrl = new URL(`/k/${tagSlug}-jobs`, baseUrl).href;

      const tagAndLocationUrl = new URL(`/sitemap/kl/${id}`, baseUrl).href;

      urls.push({
        tagUrl,
        tagName,
        tagAndLocationUrl,
      });
    });

    urls.sort((urlA, urlB) => {
      if (urlA.tagName.toUpperCase() < urlB.tagName.toUpperCase()) {
        return -1;
      }
      return 1;
    });

    return {
      props: {
        seoConfig,
        metaData,
        char,
        urls,
        canonicalUrl,
      },
    };
  }
);

export default JobTitle;
